import Logger from '../../../../../ui-kitchen/src/utils/logger';
import { getResyLocationsThunk } from '../../../../../ui-kitchen/src/reducers/resyLocationsSlice';
import {
  DEFAULT_CITY_DATA, DEFAULT_COUNTRY, GDA_ELIGIBLE, GDA_MEMBER, GDA_COOKIE_NAME, RESYVALUE_MEMBER_URL, GDA_MEMBER_URL,
  CMT_COOKIE_NAME, GOLD_CARD,
} from './constants';

import {
  RESYCREDIT_GOLD_URL, RESYCREDIT_DELTA_RESERVE_URL,
  RESYCREDIT_DELTA_PLATINUM_URL, RESYCREDIT_AMEX_PLATINUM_URL,
  CARD_TYPES,
} from '../AmexResyCredit/constants';

import {
  AMEX_GDA_PAGE,
  AMEX_RESY_CREDIT,
  GOLDCARD_NAME,
  DELTA_RESERVE_NAME,
  DELTA_PLATINUM_NAME,
  AMEX_RESY_VALUE,
  RESYVALUE_CARD_MEMBER,
  AMEX_GDA_DIRECTORY_PAGE,
  AMEX_PLATINUM_NAME,
  AMEX_PLATINUM_NIGHTS_PAGE,
} from '../../../../../ui-kitchen/src/constants/amexConstants';

import { setActiveSection } from '../../../../../ui-kitchen/src/reducers/amexEventsSlice';
import useAmexHashFragment from './useAmexHashFragment';
import { sanitizeString } from '../../../../../ui-kitchen/src/utils/sanitizeString';

import { selectIsGlobalDiningAccess } from '../../../../../ui-kitchen/src/reducers/userSlice';
import { setFunctionalCookie } from '../../../../../ui-kitchen/src/utils/resyUCMCookieUtils';

const debounce = (func, wait) => {
  let timeoutId = null;
  return (...args) => {
    const later = () => {
      clearTimeout(timeoutId);
      func(...args);
    };
    clearTimeout(timeoutId);
    timeoutId = setTimeout(later, wait);
  };
};

const getCardIcon = (awsFolderName, baseRequestUrl, iconName) => {
  if (!iconName || !awsFolderName || !baseRequestUrl) return '';
  return `${baseRequestUrl}${awsFolderName}/images/${iconName}`;
};

const normalizeItems = (items = []) => items
  .map((item) => (typeof item === 'string' ? item : item?.content))
  .filter(Boolean);

const scrollTo = (link) => {
  // keyboard support for tab focus
  const id = link.replace('#', '');
  const target = document.getElementById(id);
  // check if it's a carousel deep link
  const classes = ['AmexTabbedCarousel__tab', 'AmexTabbedCarousel__accordion--button'];
  const isTabbedCarouselEl = classes.find((i) => target?.classList.contains(i));

  if (target && !isTabbedCarouselEl) {
    // most of the target containers are non-focusable elements. Add tabindex, then set focus
    target.setAttribute('tabindex', '-1');
    setTimeout(() => target.focus({ preventScroll: true }), 0);
    setTimeout(() => target.scrollIntoView({ behavior: 'smooth' }), 100);
  }
  return false;
};

const isDevicePixelRatio2x = () => (typeof window.matchMedia === 'function' && window.matchMedia('(-webkit-min-device-pixel-ratio: 1.5), (min-resolution: 1.5dppx)').matches);

const getRetinaImage = (desktopImg, mobileImg, matchMediaDesktopBreakPoint) => {
  // returns regular image if retina is missing
  const image = matchMediaDesktopBreakPoint ? desktopImg : mobileImg;
  const isRetina = isDevicePixelRatio2x();
  if (isRetina) {
    return image?.retina || image?.regular;
  }
  return image?.regular;
};

const validateImageFileFormat = (imageName) => {
  const extensionList = ['png', 'jpg'];
  const imageExtension = imageName && imageName.split('.').pop();
  return extensionList.includes(imageExtension) ? imageName : null;
};

const updateLinksWithOriginB2B = (link, hasOriginB2B) => {
  if (hasOriginB2B) {
    const hasHash = link.includes('#');
    const isB2BLink = link.includes('global-dining-access-partner-page');
    if (hasHash) {
      const path = link.split('#');
      if (path) return `${path[0]}?origin=B2BPartner#${path[1]}`;
    }
    return isB2BLink ? link : `${link}?origin=B2BPartner`;
  }
  return link;
};

const fetchBlogData = async (blogUrl, gdaEditorialApi, moduleName) => {
  let blogDataArray = [];
  const blogURL = `${gdaEditorialApi}${blogUrl}`;

  try {
    const blogResponse = await fetch(blogURL, {
      method: 'GET',
    });
    blogDataArray = await blogResponse.json();
  } catch (err) {
    Logger.error(`[${moduleName}] Blog Fetch Error:`, err);
  }
  return blogDataArray;
};

const getBlogData = async (blogData, gdaEditorialApi, moduleName) => {
  if (!blogData) return [];
  const {
    hide_section: hideSection,
    max_count: maxCount = 0,
    blog_url: blogUrl,
  } = blogData;

  if (hideSection || (!blogUrl || blogUrl === '')) return [];
  const blogArrayResponse = await fetchBlogData(blogUrl, gdaEditorialApi, moduleName);
  if (blogArrayResponse && Array.isArray(blogArrayResponse)) {
    let updatedResponse = null;
    const editorialsMaxCount = (typeof maxCount === 'number') ? maxCount : 0;
    if (editorialsMaxCount !== 0 && (blogArrayResponse?.length > editorialsMaxCount)) {
      updatedResponse = blogArrayResponse.slice(0, editorialsMaxCount);
    } else {
      updatedResponse = blogArrayResponse;
    }
    return updatedResponse.map((item, indexItem) => ({ ...item, position: indexItem }));
  }
  return [];
};

const fetchImage = async (imageDownloadUrl, sectionLayout) => {
  try {
    const blogImageData = await fetch(imageDownloadUrl, {
      method: 'GET',
    });
    const blogResponseImageData = await blogImageData.json();
    const {
      media_details: {
        sizes: {
          'resy-lg': { source_url: imageLarge }, // 695x463 or 4/3
          'resy-md-c': { source_url: imageSmall }, // 800x450 or 16/9
          'resy-venue': { source_url: imageRegular }, // 400x400 or 1/1
          medium_large: { source_url: imageMobile }, // 768x512 or 4/3
        },
      },
    } = blogResponseImageData;
    if (sectionLayout === '1x1') {
      return {
        desktop: imageRegular,
        mobile: imageMobile,
      };
    }
    return {
      desktop_large: imageLarge,
      desktop_small: imageSmall,
      mobile: imageMobile,
    };
  } catch {
    return Logger.error('image exception');
  }
};

const fetchCity = async (cityCode, gdaEditorialApi) => {
  const cityNameUrl = `${gdaEditorialApi}city_landing/?filter[meta_key]=code&filter[meta_value]=${cityCode}`;
  let cityName = '';

  try {
    const responseCityData = await fetch(cityNameUrl, {
      method: 'GET',
    });
    const blogResponseCityData = await responseCityData.json();
    cityName = blogResponseCityData[0].title.rendered;
  } catch {
    return cityName;
  }
  return cityName;
};

const getEditorialData = async (blogDataArray, gdaEditorialApi, sectionLayout) => {
  const spotArray = ['A', 'B', 'C'];

  const blogDataResponseList = await Promise.allSettled(blogDataArray.map(async (blogItem, index) => {
    const {
      _links, link, title, id, position,
    } = blogItem;

    const imageDownloadUrl = _links['wp:featuredmedia'][0].href;
    const cityCode = blogItem['resy:market'].primary_market;
    let authorNames = '';

    try {
      let authArray = blogItem['resy:authors'];
      authArray = authArray.map((item, count) => {
        authorNames = 'By ';
        if (count !== authArray.length - 1) {
          return `${item.name} \u00B7 `;
        }
        return item.name;
      });
      for (let iCount = 0; iCount < authArray.length; iCount += 1) {
        authorNames = `${authorNames}${authArray[iCount]}`;
      }
    } catch (err) {
      Logger.error('author exception');
    }

    let cityName;
    const imageObj = await fetchImage(imageDownloadUrl, sectionLayout);

    if (cityCode || cityCode !== '') {
      cityName = await fetchCity(cityCode, gdaEditorialApi);
    }

    return {
      url: link,
      title: title.rendered,
      id,
      position,
      author: authorNames,
      image: imageObj,
      cityName: cityName || '',
      spot: spotArray[index % spotArray.length],
    };
  }));
  return blogDataResponseList
    .filter((result) => result.status === 'fulfilled' && result.value)
    .map((fulfilledResult) => fulfilledResult.value);
};

const getCurrentMomentYear = () => {
  const nowEST = moment();
  return nowEST.year();
};

const fetchS3JsonFile = async (baseRequestUrl, folderName, fileName, isNonCachingEnabled) => {
  let dataPath = '';
  if (!isNonCachingEnabled) {
    dataPath = `${baseRequestUrl}${folderName}/${fileName}`;
  } else {
    dataPath = `${baseRequestUrl}${folderName}/${fileName}?v=${Math.random()}`;
  }

  try {
    const dataResponse = await fetch(dataPath, {
      method: 'GET',
    });
    const result = await dataResponse.json();
    return result;
  } catch (err) {
    Logger.error(`[${folderName}] ${fileName} JSON File Error:`, err);
  }
  return null;
};

const validateCarouselData = (carouselConfig, requiredCarouselKeys, requiredCtaKeys) => carouselConfig
  .filter((item) => requiredCarouselKeys.every((i) => Object.prototype.hasOwnProperty.call(item, i) && item[i]))
  .reduce((acc, d) => {
    const { cta } = d;
    const hasCta = requiredCtaKeys
      .every((i) => Object.prototype.hasOwnProperty.call((cta), i) && cta[i]);
    if (hasCta) acc.push({ ...d, hasCta });
    return acc;
  }, []);

/**
 * This function is to generate the calender date range as per the JSON configuration
   "limit_selector_date": {
      "enabled": true,
      "start_date": "2024-08-19",
      "end_date": "2024-08-25"
    },
 * @param {*} startDate
 * @param {*} endDate
 * @returns
 */
const getCustomDateRange = (startDate, endDate) => {
  const dateRange = {};
  let tempDate = moment(startDate).isBefore(moment(), 'days') ? moment() : moment(startDate);

  // Difference in number of days
  const noOfDays = moment.duration(moment(endDate).diff(tempDate)).asDays() + 1;

  for (let i = 0; i < noOfDays; i += 1) {
    const dateString = tempDate.format('YYYY-MM-DD');
    let status = 'no-data';
    if (tempDate.isBefore(moment(), 'days') || tempDate.isAfter(moment(endDate), 'days')) {
      status = 'outside-window';
    }

    dateRange[dateString] = status;

    tempDate = tempDate.add(1, 'days');
  }
  return dateRange;
};

/**
 * This function is to generate the time range for the Time Picker dropdown based on JSON configurations
   "limit_selector_time": { //timeLimitSelectorValue
      "enabled": true,
      "start_time": "17:00",
      "end_time": "23:00"
    },
 * @param {*} timeLimitSelectorValue
 * @param {*} futureDateTimeOptionsValue
 * @returns
 */
const getCalculatedTimeOptions = (timeLimitSelectorValue, futureDateTimeOptionsValue) => {
  const startTime = moment(timeLimitSelectorValue?.start_time, 'HH:mm');
  const endTime = moment(timeLimitSelectorValue?.end_time, 'HH:mm');

  if (futureDateTimeOptionsValue) {
    // use memoized value if already calculated, and if dateSelected is NOT today
    return futureDateTimeOptionsValue;
  }

  const range = [{
    label: 'All Day',
    value: 'all-day',
    key: 'all-day',
  }];

  const tempTime = moment(startTime, 'HH:mm');

  while (tempTime <= endTime) {
    const timeDisplay = tempTime.format('LT');
    const timeValue = tempTime.format('HHmm');

    range.push({
      label: timeDisplay,
      value: timeValue,
      key: timeValue,
    });

    tempTime.add(30, 'minutes');
  }

  futureDateTimeOptionsValue = range;

  return range;
};

// JSON REF -  "copyright": "&copy; {currentYear} Resy Network, Inc.",
const getResyCopyright = (copyright) => copyright?.replace('{currentYear}', getCurrentMomentYear());

/**
 * Loads closest city in the Marketing page with City pills
 * @param {*} displayedCities
 * @param {*} dispatch
 * @returns
 */
const loadClosestCityCode = async (displayedCities, dispatch) => {
  let locations = [];
  try {
    locations = await dispatch(getResyLocationsThunk('all'));
  } catch (err) {
    Logger.error('Amex Experiential getResyLocationsThunk Error --', err);
  }

  const locationCodeWithUS = locations?.filter((loc) => loc.country_code === DEFAULT_COUNTRY);

  if (!locationCodeWithUS) return DEFAULT_CITY_DATA;
  const [displayedCityObj] = locationCodeWithUS?.filter((item) => displayedCities?.filter((displayedCity) => displayedCity?.code === item.code)[0]);

  if (displayedCityObj) return displayedCityObj;
  return DEFAULT_CITY_DATA;
};

const setScrolledActiveSection = (sections, dispatch) => {
  const { pageYOffset } = window;
  let newActiveSection = null;

  sections.current.forEach((section) => {
    const sectionOffsetTop = section.offsetTop;
    const sectionHeight = section.offsetHeight;

    if (pageYOffset >= (sectionOffsetTop - 150) && pageYOffset < (sectionOffsetTop + sectionHeight)) {
      newActiveSection = section.id;
    }
  });

  dispatch(setActiveSection(newActiveSection));
};

const scrollToSection = (link, offset = 0) => {
  const id = link.replace('#', '');
  const target = document.getElementById(id);
  target.setAttribute('tabindex', '-1');
  setTimeout(() => target.focus(), 0);
  setTimeout(() => {
    window.scrollTo({
      top: target?.offsetTop - offset,
      behavior: 'smooth',
    });
  }, 100);
  return false;
};

const setHashValueWithOffset = (isMobile, desktopOffset, mobileOffset, sectionId, hash, linkClicked) => {
  let offsetValue = mobileOffset;
  if (!isMobile) offsetValue = desktopOffset;
  useAmexHashFragment(offsetValue, isMobile, sectionId, hash, linkClicked);
};

const isSectionHidden = (contentData, sectionName) => contentData?.[sectionName]?.hide_section;

const convertToPlainText = (html) => {
  const tempDivElement = document.createElement('div');
  tempDivElement.innerHTML = sanitizeString(html);
  return tempDivElement.textContent || tempDivElement.innerText || '';
};

const isGDAUser = (isUserLoggedInAsGDA, Cookies) => {
  const gdaCookie = Cookies.get(GDA_COOKIE_NAME);
  let isGDA = false;

  if (isUserLoggedInAsGDA || gdaCookie === GDA_MEMBER || gdaCookie === GDA_ELIGIBLE) {
    isGDA = true;
  }
  return isGDA;
};

const filterArrayWithMandatoryItems = (requiredKeys, contentArray, sectionName) => {
  const filteredArray = contentArray
    .filter((item) => {
      const hasPresent = requiredKeys.every((i) => (Object.prototype.hasOwnProperty.call(item, i)
        && item[i]));
      return hasPresent || Logger.error(`${sectionName} is missing field in object - ${sanitizeString(JSON.stringify(item))}`);
    });
  return filteredArray;
};

const checkJsonObjectMandatory = (requiredKeys, sectionConfig, sectionName) => {
  let isMissing = false;
  requiredKeys.forEach((i) => {
    if (!(Object.prototype.hasOwnProperty.call(sectionConfig, i) && sectionConfig[i])) {
      isMissing = true;
      Logger.error(`${sectionName} is missing key - ${i}`);
    }
  });
  return !isMissing;
};

const chunkArray = (array, chunkSize) => {
  const result = [];
  for (let i = 0; i < array.length; i += chunkSize) {
    result.push(array.slice(i, i + chunkSize));
  }
  return result;
};

const identifyPageNameWithUrl = ($location, metadata) => {
  const locationPath = $location.path();
  const pathArr = locationPath?.split('/');
  let urlPathValue = '';
  if (pathArr?.length === 2) {
    [, urlPathValue] = pathArr;
    if (urlPathValue === RESYVALUE_MEMBER_URL || urlPathValue === GDA_MEMBER_URL) {
      return metadata?.pageName;
    }
  }
  return '';
};

const handleMktAnalytics = (event, eventName, extraParams, callBack) => {
  let eventAssetValue = null;
  if (extraParams) {
    const {
      action_name: actionName,
      tab_item: tabItem,
      item_name: itemName,
      event_mode: eventMode,
    } = extraParams;

    const actionNameStr = actionName ? `Action Name : ${actionName}` : '';
    const tabItemStr = tabItem ? ` > Tab Item : ${tabItem}` : '';
    const itemNameStr = itemName ? ` > Item Name : ${itemName}` : '';
    const eventModeStr = eventMode ? ` > Event Mode : ${eventMode}` : '';

    eventAssetValue = `${actionNameStr}${tabItemStr}${itemNameStr}${eventModeStr}`;
  }

  const params = {
    ...(extraParams || {}),
    ...eventAssetValue && { eventAsset: eventAssetValue },
  };

  event(eventName, params);
  if (callBack) setTimeout(callBack, 1000);
};

/**
 * Centers horizontally page menu links after click.
 * @param {number} index Link index in the container.
 * @param {string} containerId Container id.
 * @param {number} timeout Timeout value.
 */
const handleMenuLinkScroll = (index, containerId, timeout) => {
  const menuContainer = document.getElementById(containerId);
  if (menuContainer) {
    // center selected link
    setTimeout(() => {
      const activeLink = menuContainer.children[index];
      activeLink.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
    }, timeout);
  }
};

const getScrollBehaviorFromMotionPreference = () => {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return 'smooth';
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  return prefersReducedMotion ? 'auto' : 'smooth';
};

const handle404Redirect = () => {
  // 404 error page
  window.location.pathname = '/404';
  return null;
};

// Month filter
const getMonthOptions = (venuesMonths, newMonthOptions) => {
  const MONTH_OPTIONS = [];

  const MONTH_LABELS = moment().localeData().months();

  const padZero = (num) => (num < 10 ? '0' : '') + num;

  for (let i = 0; i < 12; i += 1) {
    const str = padZero(i + 1);
    const label = MONTH_LABELS[i];
    MONTH_OPTIONS.push({
      key: str, value: str, label, orderId: i + 1,
    });
  }

  MONTH_OPTIONS.forEach((element) => {
    if (venuesMonths.find((venueMon) => element.label === venueMon.label)) {
      newMonthOptions.push(element);
    }
  });
  const sortedMonths = newMonthOptions.sort((a, b) => a.orderId - b.orderId);
  return sortedMonths;
};

// city filter
const getCityOptions = (locations) => locations
  .map((city, index) => ({
    key: city.url_slug,
    value: city.url_slug,
    label: city.name,
    orderId: index,
  }));

export const setGDAPageCookie = (locationValue, isAuthenticated, Cookies, getState) => {
  const locationPath = locationValue.path();
  const expires = moment().add(365, 'days').toDate();

  if (isAuthenticated && getState()?.user?.userData) {
    const isUserLoggedInAsGDA = selectIsGlobalDiningAccess(getState());
    if (isUserLoggedInAsGDA) {
      return setFunctionalCookie(GDA_COOKIE_NAME, GDA_MEMBER, { expires });
    }
  }

  if (locationPath === '/global-dining-access-member') {
    const gdaCookie = Cookies.get(GDA_COOKIE_NAME);
    if (!gdaCookie || gdaCookie !== GDA_MEMBER) {
      return setFunctionalCookie(GDA_COOKIE_NAME, GDA_ELIGIBLE, { expires });
    }
  }

  return null;
};

export const getKeyByValue = (object, value) => Object.keys(object).find((key) => object[key] === value);

// started
const getResyCreditCardTypes = (getState, resyCreditCardTypes) => {
  const userData = getState()?.user?.userData;

  const userAmexCardTypes = userData?.amex_card_types || [];
  const resyCreditCardTypeArr = Object.values(resyCreditCardTypes);
  const setUserAmexCardTypes = new Set(userAmexCardTypes);

  for (let i = 0; i < resyCreditCardTypeArr.length; i++) {
    if (resyCreditCardTypeArr[i] instanceof Array) {
      const hasCommonValue = resyCreditCardTypeArr[i].some((item) => setUserAmexCardTypes.has(item));
      if (hasCommonValue) return getKeyByValue(resyCreditCardTypes, resyCreditCardTypeArr[i]);
    } else {
      for (let j = 0; j < userData?.amex_card_types.length; j++) {
        if (resyCreditCardTypeArr[i] === userAmexCardTypes[j]) {
          return resyCreditCardTypeArr[i];
        }
      }
    }
  }
  return '';
};

const getGDALabelValue = (isAuthenticated, isGDA, Cookies) => {
  let labelValue = '';
  if (isAuthenticated && isGDA) {
    labelValue = GDA_MEMBER;
  } else if (Cookies.get(GDA_COOKIE_NAME)) {
    labelValue = Cookies.get(GDA_COOKIE_NAME);
  }
  return labelValue;
};

const getUserCardTypeValue = (resyCreditCardTypes, cardType) => {
  let labelValue = '';
  if (cardType === resyCreditCardTypes.GOLD) {
    labelValue = GOLDCARD_NAME;
  } else if (cardType === resyCreditCardTypes.DELTARESERVE) {
    labelValue = DELTA_RESERVE_NAME;
  } else if (cardType === resyCreditCardTypes.DELTAPLATINUM) {
    labelValue = DELTA_PLATINUM_NAME;
  } else if (cardType === CARD_TYPES.AMEXPLATINUM) {
    labelValue = AMEX_PLATINUM_NAME;
  }
  return labelValue;
};

const getPageValues = (metadata, location,
  isAuthenticated, Cookies, isGDA, getState) => {
  const pageName = metadata?.pageName;

  const authenticated = typeof isAuthenticated === 'object'
    ? isAuthenticated.isAuthenticated() : isAuthenticated;

  let labelValue = '';
  const resyCreditCardTypes = getState()?.amexGDAEvents?.resyCreditCardTypes;
  const cardType = isAuthenticated ? getResyCreditCardTypes(getState, resyCreditCardTypes) : '';

  if (pageName === AMEX_RESY_CREDIT) {
    const locationPath = location.path();
    if ((Cookies.get(CMT_COOKIE_NAME) === GOLD_CARD) || locationPath === RESYCREDIT_GOLD_URL || (isAuthenticated && cardType === resyCreditCardTypes.GOLD)) {
      labelValue = GOLDCARD_NAME;
    } else if (locationPath === RESYCREDIT_DELTA_RESERVE_URL || (isAuthenticated && cardType === resyCreditCardTypes.DELTARESERVE)) {
      labelValue = DELTA_RESERVE_NAME;
    } else if (locationPath === RESYCREDIT_DELTA_PLATINUM_URL || (isAuthenticated && cardType === resyCreditCardTypes.DELTAPLATINUM)) {
      labelValue = DELTA_PLATINUM_NAME;
    } else if (locationPath === RESYCREDIT_AMEX_PLATINUM_URL || (isAuthenticated && cardType === CARD_TYPES.AMEXPLATINUM)) {
      labelValue = AMEX_PLATINUM_NAME;
    } else {
      labelValue = getGDALabelValue(isAuthenticated, isGDA, Cookies);
    }
  } else if (pageName === AMEX_RESY_VALUE) {
    if (AMEX_RESY_VALUE === identifyPageNameWithUrl(location, metadata)) {
      labelValue = RESYVALUE_CARD_MEMBER;
    } else if (Cookies.get(CMT_COOKIE_NAME) === GOLD_CARD) {
      labelValue = GOLDCARD_NAME;
    } else if (isAuthenticated && cardType !== '') {
      labelValue = getUserCardTypeValue(resyCreditCardTypes, cardType);
    } else {
      labelValue = getGDALabelValue(isAuthenticated, isGDA, Cookies);
    }
  } else if (pageName === AMEX_GDA_PAGE) {
    if (AMEX_GDA_PAGE === identifyPageNameWithUrl(location, metadata) && (isAuthenticated && isGDA)) {
      labelValue = GDA_MEMBER;
    } else if (AMEX_GDA_PAGE === identifyPageNameWithUrl(location, metadata)) {
      labelValue = GDA_ELIGIBLE;
    } else if (isAuthenticated && cardType !== '') {
      labelValue = getUserCardTypeValue(resyCreditCardTypes, cardType);
    } else {
      labelValue = getGDALabelValue(isAuthenticated, isGDA, Cookies);
    }
  } else if (pageName === AMEX_GDA_DIRECTORY_PAGE) {
    labelValue = getGDALabelValue(isAuthenticated, isGDA, Cookies);
  } else if (pageName === AMEX_PLATINUM_NIGHTS_PAGE) {
    if (Cookies.get(CMT_COOKIE_NAME) === GOLD_CARD) {
      labelValue = GOLDCARD_NAME;
    } else if (isAuthenticated && cardType === CARD_TYPES.AMEXPLATINUM) {
      labelValue = AMEX_PLATINUM_NAME;
    } else if (isAuthenticated && cardType !== '') {
      labelValue = getUserCardTypeValue(resyCreditCardTypes, cardType);
    } else {
      labelValue = getGDALabelValue(isAuthenticated, isGDA, Cookies);
    }
  }

  return {
    category: authenticated ? 'LoggedIn' : 'Guest',
    label: labelValue,
    pageName,
  };
};

export const getMktPageAnalytics = (metadata, location,
  isAuthenticated, Cookies, isGDA, getState) => {
  const pageValues = getPageValues(metadata, location,
    isAuthenticated, Cookies, isGDA, getState);

  const pageEvent = pageValues?.pageName
    ? `Viewed ${pageValues?.pageName} page` : 'Viewed page';

  const customData = [pageEvent, {
    path: location.path(),
    language: window.preferredLanguage,
    category: pageValues?.category,
    label: pageValues?.label,
  }, metadata, getState()?.user?.userData];

  return customData;
};

const getIsFAQsHidden = (faqs) => faqs?.hide_section || !faqs?.title;

const isPlainObject = (object) => object !== null && typeof object === 'object' && !Array.isArray(object);

// compare two objects and use the property from the second object whenever it exists.
const deepMergeDefined = (firstObj, secondObj) => {
  // If second exists (including null/false/0/"") it wins
  if (secondObj !== undefined) {
    if (isPlainObject(firstObj) && isPlainObject(secondObj)) {
      const out = { ...firstObj };
      Object.keys(secondObj).forEach((key) => {
        out[key] = deepMergeDefined(firstObj?.[key], secondObj[key]);
      });
      return out;
    }
    return secondObj;
  }

  // second is undefined -> keep first
  return firstObj;
};

export {
  debounce,
  getCardIcon,
  normalizeItems,
  scrollTo,
  isDevicePixelRatio2x,
  getRetinaImage,
  validateImageFileFormat,
  updateLinksWithOriginB2B,
  getBlogData,
  getCurrentMomentYear,
  fetchS3JsonFile,
  validateCarouselData,
  getCustomDateRange,
  getCalculatedTimeOptions,
  getResyCopyright,
  loadClosestCityCode,
  setScrolledActiveSection,
  scrollToSection,
  setHashValueWithOffset,
  isSectionHidden,
  convertToPlainText,
  isGDAUser,
  filterArrayWithMandatoryItems,
  checkJsonObjectMandatory,
  chunkArray,
  identifyPageNameWithUrl,
  handleMktAnalytics,
  handleMenuLinkScroll,
  getScrollBehaviorFromMotionPreference,
  handle404Redirect,
  getMonthOptions,
  getCityOptions,
  getEditorialData,
  getIsFAQsHidden,
  deepMergeDefined,
};
